import { Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useDebounce } from "use-debounce"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-regular-svg-icons"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import type { TranslationKey } from "../../../../../../modules/localization/translations"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { StandardButton } from "../../../../buttons/StandardButton"
import type { SegmentedControlOption } from "../../../../segmentedControl/StandardSegmentedControl"
import { StandardSegmentedControl } from "../../../../segmentedControl/StandardSegmentedControl"

import { useTimelineSide } from "../context/RefreshCalendarProvider"
import type { MeetingWindow } from "./useMeetingAvailability"

// outlasts the timeline's own first scroll to the meeting, so the button does not flash on open
const SIDE_SETTLE_MS = 500

const useStyles = makeStyles()((theme) => ({
    banner: {
        flexWrap: "wrap",
        width: "fit-content",
        maxWidth: "100%",
        minWidth: 0,
        padding: ".375rem .375rem .375rem .875rem",
        backgroundColor: theme.palette.tailwind.amber[50],
        border: `1px solid ${theme.palette.tailwind.amber[300]}`,
        borderRadius: ".75rem",
    },
    meeting: {
        flex: "0 1 auto",
        minWidth: 0,
    },
    title: {
        fontWeight: 600,
        lineHeight: 1.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        color: theme.palette.tailwind.slate[900],
    },
    summary: {
        lineHeight: 1.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        color: theme.palette.tailwind.slate[600],
    },
    // matches the toolbar's .fc-button (Today, Go to date)
    goToMeetingButton: {
        flex: "none",
        whiteSpace: "nowrap",
        fontSize: ".875rem",
        fontWeight: 500,
        borderRadius: ".625rem",
        "&&": {
            color: theme.palette.tailwind.slate[950],
            backgroundColor: theme.palette.common.white,
            borderColor: theme.palette.tailwind.slate[300],
        },
        "&&:hover": {
            backgroundColor: theme.palette.tailwind.slate[50],
            borderColor: theme.palette.tailwind.slate[400],
        },
    },
}))

enum MeetingFitMode {
    FITS = "fits",
    ALL = "all",
}

type FitCriterion = "seats" | "availability" | "seatsAndAvailability"

type FitTooltipCopy = Readonly<{
    id: TranslationKey
    text: string
}>

const FITS_TOOLTIP_COPY: Readonly<Record<FitCriterion, FitTooltipCopy>> = {
    seats: {
        id: "create-order-timeline-meeting-fit-banner-fits-segment-tooltip-seats",
        text: "Shows only resources with enough seats for the attendees",
    },
    availability: {
        id: "create-order-timeline-meeting-fit-banner-fits-segment-tooltip-availability",
        text: "Shows only resources that are free at the meeting time",
    },
    seatsAndAvailability: {
        id: "create-order-timeline-meeting-fit-banner-fits-segment-tooltip-seats-and-availability",
        text: "Shows only resources with enough seats that are free at the meeting time",
    },
}

type MeetingFitBannerProps = Readonly<{
    meetingName?: string
    meetingWindow: MeetingWindow | null
    guestCount: number
    fitsCount: number
    allCount: number
    isOn: boolean
    onToggle: (next: boolean) => void
    /** Omitted when the banner shows no meeting time to go to. */
    onGoToMeeting?: () => void
}>

export function MeetingFitBanner({
    meetingName,
    meetingWindow,
    guestCount,
    fitsCount,
    allCount,
    isOn,
    onToggle,
    onGoToMeeting,
}: MeetingFitBannerProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const meetingSide = useTimelineSide(meetingWindow)
    const [settledSide] = useDebounce(meetingSide, SIDE_SETTLE_MS)
    const isOutOfView = (meetingSide === "before" || meetingSide === "after") && meetingSide === settledSide

    const renderTitle = () =>
        meetingName ? (
            <Typography variant="body2" title={meetingName} className={classes.title}>
                {meetingName}
            </Typography>
        ) : (
            <LocalizedStrict id="create-order-timeline-meeting-fit-banner-title-fallback">
                <Typography variant="body2" className={classes.title}>
                    Your meeting
                </Typography>
            </LocalizedStrict>
        )

    const renderSummary = () => {
        if (!meetingWindow) {
            return (
                <LocalizedStrict
                    id="create-order-timeline-meeting-fit-banner-summary-attendees-only"
                    vars={{ count: guestCount }}
                >
                    <Typography variant="caption" className={classes.summary}>
                        {guestCount}
                    </Typography>
                </LocalizedStrict>
            )
        }

        const start = dateTimeFactory.fromISO(meetingWindow.start)
        const vars = {
            date: `${dateTimeFactory.formatCustom(start, "ccc")} ${dateTimeFactory.formatMonthDay(start)}`,
            time: dateTimeFactory.formatTimeRange(start, dateTimeFactory.fromISO(meetingWindow.end)),
        }

        return guestCount > 0 ? (
            <LocalizedStrict
                id="create-order-timeline-meeting-fit-banner-summary"
                vars={{ ...vars, count: guestCount }}
            >
                <Typography variant="caption" className={classes.summary}>
                    {vars.date}, {vars.time}
                </Typography>
            </LocalizedStrict>
        ) : (
            <LocalizedStrict id="create-order-timeline-meeting-fit-banner-summary-no-attendees" vars={vars}>
                <Typography variant="caption" className={classes.summary}>
                    {vars.date}, {vars.time}
                </Typography>
            </LocalizedStrict>
        )
    }

    // Seats narrowing needs attendees, availability narrowing needs a known time — promise only what applies.
    const fitsTooltip =
        FITS_TOOLTIP_COPY[!meetingWindow ? "seats" : guestCount > 0 ? "seatsAndAvailability" : "availability"]

    const options: ReadonlyArray<SegmentedControlOption<MeetingFitMode>> = [
        {
            value: MeetingFitMode.FITS,
            label: (
                <LocalizedStrict id="create-order-timeline-meeting-fit-banner-fits-segment">
                    <>Match your meeting</>
                </LocalizedStrict>
            ),
            badge: fitsCount.toString(),
            tooltip: (
                <LocalizedStrict id={fitsTooltip.id}>
                    <>{fitsTooltip.text}</>
                </LocalizedStrict>
            ),
        },
        {
            value: MeetingFitMode.ALL,
            label: (
                <LocalizedStrict id="create-order-timeline-meeting-fit-banner-all-segment">
                    <>All resources</>
                </LocalizedStrict>
            ),
            badge: allCount.toString(),
            tooltip: (
                <LocalizedStrict id="create-order-timeline-meeting-fit-banner-all-segment-tooltip">
                    <>Shows all resources, including ones that don&apos;t fit the meeting</>
                </LocalizedStrict>
            ),
        },
    ]

    const handleChangeMode = (mode: MeetingFitMode) => {
        const next = mode === MeetingFitMode.FITS
        if (next !== isOn) onToggle(next)
    }

    return (
        <Stack direction="row" alignItems="center" columnGap="1.25rem" rowGap=".25rem" className={classes.banner}>
            <Stack className={classes.meeting}>
                {renderTitle()}
                {renderSummary()}
            </Stack>

            <StandardSegmentedControl
                options={options}
                value={isOn ? MeetingFitMode.FITS : MeetingFitMode.ALL}
                onChange={handleChangeMode}
                appearance="track"
                size="small"
            />

            {onGoToMeeting && isOutOfView && (
                <LocalizedStrict id="create-order-timeline-meeting-fit-banner-go-to-meeting-button">
                    <StandardButton
                        variant="outlined"
                        color="inherit"
                        fullWidth={false}
                        size="small"
                        updateDefaultClasses={classes.goToMeetingButton}
                        startIcon={meetingSide === "before" ? <FontAwesomeIcon icon={faChevronLeft} /> : undefined}
                        endIcon={meetingSide === "after" ? <FontAwesomeIcon icon={faChevronRight} /> : undefined}
                        onClick={onGoToMeeting}
                    >
                        Go to meeting
                    </StandardButton>
                </LocalizedStrict>
            )}
        </Stack>
    )
}
